import { getPoints } from '@/services/points.service';
import { getCategoriesByPoint, getMerchantCategories } from '@/services/categories.axios.service';
import { getGoodsRead } from '@/services/goods.service';
import { integrateMenu, getMenuIntegrationsList } from '@/services/menu.axios.service';
import { constructCategoriesDTO } from '@/utils/categories.utils';
import { listSections } from '@/services/sections.axios.service';

function normalizePoints(points = []) {
  return points.map((point) => ({
    ...point,
    title: point.title || point.name || 'Point',
  }));
}

function normalizeGoodsForPoint(goods = [], pointId) {
  return goods.map((product) => ({
    ...product,
    pointId: product.pointId || pointId,
  }));
}

function buildPointCatalog(point, categories = [], goods = [], sections = []) {
  const [catalogPoint] = constructCategoriesDTO([point], categories, normalizeGoodsForPoint(goods, point.id));

  return {
    ...point,
    categories: catalogPoint?.categories || [],
    sections,
  };
}

async function loadPointCategories(pointId) {
  const merchantCategories = await getMerchantCategories({ pointId });

  if (Array.isArray(merchantCategories) && merchantCategories.length > 0) {
    return merchantCategories;
  }

  return getCategoriesByPoint({ pointId });
}

function getDefaultMenuIntegrationState() {
  return {
    taskId: null,
    status: 'idle',
    result: null,
  };
}

function defaultState() {
  return {
    points: [],
    currentPointId: null,
    isPointsLoaded: false,
    isPointsLoading: false,
    pointsRequest: null,
    pointCatalogById: {},
    pointCatalogLoadingById: {},
    pointCatalogRequestById: {},
    menuIntegrationTaskIdByPointId: {},
    menuIntegrationByPointId: {},
  };
}

export default {
  namespaced: true,
  state: defaultState(),
  getters: {
    points(state) {
      return state.points;
    },
    currentPointId(state) {
      return state.currentPointId;
    },
    currentPoint(state) {
      return state.points.find((point) => point.id === state.currentPointId) || null;
    },
    catalogByPoint: (state) => (pointId) => state.pointCatalogById[pointId] || null,
    currentPointCatalog(state) {
      return state.currentPointId ? state.pointCatalogById[state.currentPointId] || null : null;
    },
    currentPointCategories(state, getters) {
      return getters.currentPointCatalog?.categories || [];
    },
    productById: (state) => (pointId, productId) => {
      const pointCatalog = state.pointCatalogById[pointId];
      if (!pointCatalog?.categories?.length) return null;

      for (const category of pointCatalog.categories) {
        const product = category.products?.find((item) => item.id === productId);
        if (product) return product;
      }

      return null;
    },
    isPointCatalogLoaded: (state) => (pointId) => !!state.pointCatalogById[pointId],
    isPointCatalogLoading: (state) => (pointId) => !!state.pointCatalogLoadingById[pointId],
    menuIntegrationTaskIdByPoint: (state) => (pointId) => state.menuIntegrationTaskIdByPointId[pointId] || null,
    menuIntegrationByPoint: (state) => (pointId) =>
      state.menuIntegrationByPointId[pointId] || getDefaultMenuIntegrationState(),
    currentPointMenuIntegrationTaskId(state) {
      return state.currentPointId ? state.menuIntegrationTaskIdByPointId[state.currentPointId] || null : null;
    },
    currentPointMenuIntegration(state) {
      return state.currentPointId
        ? state.menuIntegrationByPointId[state.currentPointId] || getDefaultMenuIntegrationState()
        : getDefaultMenuIntegrationState();
    },
  },
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, defaultState());
    },
    SET_POINTS(state, points) {
      state.points = normalizePoints(points);
      state.isPointsLoaded = true;
    },
    SET_POINTS_LOADING(state, status) {
      state.isPointsLoading = status;
    },
    SET_POINTS_REQUEST(state, request) {
      state.pointsRequest = request;
    },
    SET_CURRENT_POINT_ID(state, pointId) {
      state.currentPointId = pointId || null;
    },
    SET_POINT_CATALOG_LOADING(state, { pointId, status }) {
      state.pointCatalogLoadingById = {
        ...state.pointCatalogLoadingById,
        [pointId]: status,
      };
    },
    SET_POINT_CATALOG_REQUEST(state, { pointId, request }) {
      state.pointCatalogRequestById = {
        ...state.pointCatalogRequestById,
        [pointId]: request,
      };
    },
    SET_POINT_CATALOG(state, { pointId, pointCatalog }) {
      state.pointCatalogById = {
        ...state.pointCatalogById,
        [pointId]: pointCatalog,
      };
      state.points = state.points.map((point) => (point.id === pointId ? pointCatalog : point));
    },
    INVALIDATE_POINT_CATALOG(state, pointId) {
      const nextCatalog = { ...state.pointCatalogById };
      const nextLoading = { ...state.pointCatalogLoadingById };
      const nextRequests = { ...state.pointCatalogRequestById };

      delete nextCatalog[pointId];
      delete nextLoading[pointId];
      delete nextRequests[pointId];

      state.pointCatalogById = nextCatalog;
      state.pointCatalogLoadingById = nextLoading;
      state.pointCatalogRequestById = nextRequests;
      state.points = state.points.map((point) => {
        if (point.id !== pointId) return point;

        return {
          ...point,
          categories: [],
        };
      });
    },
    SET_MENU_INTEGRATION_TASK_ID(state, { pointId, taskId }) {
      state.menuIntegrationTaskIdByPointId = {
        ...state.menuIntegrationTaskIdByPointId,
        [pointId]: taskId || null,
      };
    },
    SET_MENU_INTEGRATION_STATE(state, { pointId, payload }) {
      state.menuIntegrationByPointId = {
        ...state.menuIntegrationByPointId,
        [pointId]: {
          ...getDefaultMenuIntegrationState(),
          ...state.menuIntegrationByPointId[pointId],
          ...payload,
        },
      };
    },
  },
  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },
    async loadPoints({ commit, state }) {
      if (state.isPointsLoaded) {
        return state.points;
      }

      if (state.isPointsLoading) {
        return state.pointsRequest || state.points;
      }

      commit('SET_POINTS_LOADING', true);
      const request = getPoints();
      commit('SET_POINTS_REQUEST', request);

      try {
        const points = await request;
        commit('SET_POINTS', Array.isArray(points) ? points : points?.data || []);
        return state.points;
      } finally {
        commit('SET_POINTS_REQUEST', null);
        commit('SET_POINTS_LOADING', false);
      }
    },
    async loadPointCatalog({ commit, dispatch, getters, state }, { pointId, forceRefresh = false } = {}) {
      if (!pointId) return null;

      if (getters.isPointCatalogLoaded(pointId) && !forceRefresh) {
        return getters.catalogByPoint(pointId);
      }

      if (getters.isPointCatalogLoading(pointId)) {
        return state.pointCatalogRequestById[pointId] || getters.catalogByPoint(pointId);
      }

      if (!state.isPointsLoaded) {
        await dispatch('loadPoints');
      }

      const basePoint = state.points.find((point) => point.id === pointId) || {
        id: pointId,
        title: 'Point',
      };

      commit('SET_POINT_CATALOG_LOADING', { pointId, status: true });
      const request = Promise.all([
        loadPointCategories(pointId),
        getGoodsRead({ pointId, deleted: false }),
        listSections({ pointId }).catch(() => ({ data: [] })),
      ]).then(([categories, goodsResponse, sectionsResponse]) =>
        buildPointCatalog(
          basePoint,
          categories || [],
          goodsResponse?.data || [],
          sectionsResponse?.data || [],
        ),
      );
      commit('SET_POINT_CATALOG_REQUEST', { pointId, request });

      try {
        const pointCatalog = await request;
        commit('SET_POINT_CATALOG', { pointId, pointCatalog });
        return pointCatalog;
      } finally {
        commit('SET_POINT_CATALOG_REQUEST', { pointId, request: null });
        commit('SET_POINT_CATALOG_LOADING', { pointId, status: false });
      }
    },
    async selectPoint({ commit, dispatch }, { pointId, forceRefresh = false } = {}) {
      commit('SET_CURRENT_POINT_ID', pointId);

      if (!pointId) return null;

      return dispatch('loadPointCatalog', { pointId, forceRefresh });
    },
    invalidatePointCatalog({ commit }, pointId) {
      if (!pointId) return;
      commit('INVALIDATE_POINT_CATALOG', pointId);
    },
    async integratePointMenu({ commit }, { pointId } = {}) {
      if (!pointId) return null;

      commit('SET_MENU_INTEGRATION_STATE', {
        pointId,
        payload: {
          taskId: null,
          status: 'pending',
          result: null,
        },
      });

      try {
        const response = await integrateMenu({ pointId });
        const taskId = response?.data?.taskId || null;

        commit('SET_MENU_INTEGRATION_TASK_ID', { pointId, taskId });
        commit('SET_MENU_INTEGRATION_STATE', {
          pointId,
          payload: {
            taskId,
            status: taskId ? 'pending' : 'error',
            result: null,
          },
        });
        return response;
      } catch (error) {
        commit('SET_MENU_INTEGRATION_TASK_ID', { pointId, taskId: null });
        commit('SET_MENU_INTEGRATION_STATE', {
          pointId,
          payload: {
            taskId: null,
            status: 'error',
            result: null,
          },
        });
        throw error;
      }
    },
    async fetchMenuIntegrationStatus({ commit, getters }, { pointId, taskId } = {}) {
      const nextTaskId = taskId || getters.menuIntegrationTaskIdByPoint(pointId);
      if (!pointId || !nextTaskId) return null;

      const response = await getMenuIntegrationsList({});
      const integrations = Array.isArray(response?.data) ? response.data : [];
      const integration = integrations.find((item) => item.id === nextTaskId && item.pointId === pointId) || null;

      commit('SET_MENU_INTEGRATION_STATE', {
        pointId,
        payload: {
          taskId: nextTaskId,
          status: integration?.finishedAt ? 'success' : 'pending',
          result: integration,
        },
      });

      return integration;
    },
  },
};
